import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useCurrentVenue } from '../../hooks/useCurrentVenue';
import { useAuthStore } from '../../store/auth.store';
import type { MemberLevelImportPreview as Preview } from '@pokerroom/types';
export function MemberLevelTransferPanel(props: { onImported: () => void }) {
  const { venue } = useCurrentVenue();
  return <MemberLevelTransferForm key={venue?.id} venueId={venue?.id} {...props} />;
}
function MemberLevelTransferForm({
  onImported,
  venueId,
}: {
  onImported: () => void;
  venueId?: string;
}) {
  const canManage = useAuthStore((s) => s.allowedActions?.includes('member:level_manage'));
  const [pkg, setPackage] = useState<unknown>();
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<Preview>();
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setPackage(undefined);
    setChoices({});
    setPreview(undefined);
    setMessage('');
  }, [venueId]);
  if (!canManage) return null;
  async function exportLevels() {
    setBusy(true);
    setMessage('');
    try {
      const result = await api.get('/members/levels/export');
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' }),
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = 'member-levels.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setMessage('匯出失敗，請稍後重試。');
    } finally {
      setBusy(false);
    }
  }
  async function load(file?: File) {
    setPackage(undefined);
    setPreview(undefined);
    setChoices({});
    setMessage('');
    if (!file) return;
    try {
      if (file.size > 200_000) throw Error();
      setPackage(JSON.parse(await file.text()));
    } catch {
      setMessage('請選擇有效的 JSON 等級資料，檔案上限 200 KB。');
    }
  }
  async function inspect() {
    setBusy(true);
    setMessage('');
    setPreview(undefined);
    try {
      setPreview(
        await api.post<Preview>('/members/levels/import/preview', { package: pkg, choices }),
      );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '預覽失敗，請檢查預設與 Z客等級設定。');
    } finally {
      setBusy(false);
    }
  }
  async function confirm() {
    if (!preview) return;
    setBusy(true);
    setMessage('');
    try {
      await api.post('/members/levels/import/confirm', {
        package: pkg,
        choices,
        digest: preview.digest,
      });
      setPreview(undefined);
      setPackage(undefined);
      setMessage('會員等級已匯入。');
      onImported();
    } catch {
      setPreview(undefined);
      setMessage('匯入未完成；請重新預覽，確認資料與權限。');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="mb-6 rounded-lg border border-base-300 p-4" aria-label="會員等級匯入匯出">
      <h2 className="font-bold">會員等級匯入匯出</h2>
      <button className="btn btn-sm my-2" disabled={busy} onClick={exportLevels}>
        匯出 JSON
      </button>
      <label className="block">
        匯入 JSON
        <input
          key={venueId}
          type="file"
          accept=".json,application/json"
          disabled={busy}
          onChange={(e) => void load(e.target.files?.[0])}
        />
      </label>
      {pkg !== undefined && (
        <button className="btn btn-sm my-2" disabled={busy} onClick={inspect}>
          預覽匯入
        </button>
      )}
      {preview && (
        <>
          {preview.scope && (
            <p>
              {preview.scope === 'venue-policy'
                ? '此舊版檔案只更新目前場館點數政策，共用資格維持不變。'
                : '此新版檔案包含協會共用資格與目前場館點數政策；更新資格將套用全部場館。'}
            </p>
          )}
          <p>相同代碼有差異時，預設保留現有資料；選擇更新後請重新預覽。</p>
          {preview.rows.map((row) => (
            <div key={row.code} className="my-2">
              <strong>{row.code}</strong>：
              {row.status === 'create' ? '新增' : row.status === 'identical' ? '相同' : '有差異'}
              {row.status === 'conflict' && (
                <select
                  aria-label={`${row.code} 匯入方式`}
                  value={choices[row.code] ?? 'keep'}
                  disabled={busy}
                  onChange={(e) => {
                    setChoices({ ...choices, [row.code]: e.target.value });
                    setPreview(undefined);
                  }}
                >
                  <option value="keep">保留現有</option>
                  <option value="update">更新</option>
                </select>
              )}
              <details>
                <summary>檢視前後資料</summary>
                <pre className="overflow-auto">
                  {JSON.stringify(
                    preview.scope
                      ? {
                          共用資格: {
                            更新前: row.qualificationBefore,
                            更新後: row.qualificationAfter,
                          },
                          館別政策: { 更新前: row.venuePolicyBefore, 更新後: row.venuePolicyAfter },
                        }
                      : { before: row.before, after: row.after },
                    null,
                    2,
                  )}
                </pre>
              </details>
            </div>
          ))}
          {preview.errors.map((error) => (
            <p role="alert" key={error}>
              {error}
            </p>
          ))}
          <button
            className="btn btn-primary btn-sm"
            disabled={busy || preview.errors.length > 0}
            onClick={confirm}
          >
            確認匯入
          </button>
        </>
      )}
      {message && <p role="status">{message}</p>}
    </section>
  );
}
